import { SectionKey } from "@reactive-resume/schema";
import { create } from "zustand";

export type DialogName = "resume" | "lock" | "import" | "two-factor" | "ai-csv-import" | SectionKey;

export type DialogMode = "create" | "update" | "duplicate" | "delete";

export type DialogPayload<T = unknown> = {
  id: DialogName;
  item?: T;
};

type Dialog<T = unknown> = {
  name: DialogName;
  mode: DialogMode;
  payload?: DialogPayload<T>;
};

interface DialogState {
  dialog: Dialog | null;
}

interface DialogActions {
  setDialog: <T>(dialog: Dialog<T> | null) => void;
}

export const useDialogStore = create<DialogState & DialogActions>()((set) => ({
  dialog: null,
  setDialog: (dialog) => set({ dialog }),
}));

export const useDialog = <T = unknown>(name: DialogName) => {
  const dialog = useDialogStore((state) => {
    const d = state.dialog;
    if (!d) return null;
    const normalizedName =
      typeof name === "string" && name.startsWith("custom.") ? "custom" : name;
    if (d.name === normalizedName || d.name === name) return d;
    return null;
  });

  return {
    isOpen: !!dialog,
    mode: dialog?.mode,
    payload: dialog?.payload as DialogPayload<T>,
    open: (mode: DialogMode, payload?: DialogPayload<T>) => {
      const dialogName =
        typeof name === "string" && name.startsWith("custom.") ? "custom" : name;
      useDialogStore.setState({ dialog: { name: dialogName, mode, payload } });
    },
    close: () => useDialogStore.setState({ dialog: null }),
  };
};
